import { useCallback, useEffect, useRef, useState } from 'react';

export const FOCUSABLE_SELECTOR =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

type UseOverlayFocusReturn = {
  contentRef: (node: HTMLElement | null) => void;
};

/**
 * Manages focus for overlay/modal components:
 * - Captures the element that had focus before the overlay opened
 * - Moves focus to the first focusable element inside the overlay content
 * - Restores focus to the original trigger element when the overlay unmounts
 */
const useOverlayFocus = (): UseOverlayFocusReturn => {
  const triggerRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    triggerRef.current = null;
    if (process.env.JS_ENV_CLIENT) {
      triggerRef.current = document.activeElement as HTMLElement;
    }

    return () => {
      triggerRef.current?.focus();
    };
  }, []);

  const [contentNode, setContentNode] = useState<HTMLElement | null>(null);
  const contentRef = useCallback((node: HTMLElement | null) => {
    setContentNode(node);
  }, []);

  useEffect(() => {
    if (!contentNode) return;

    const firstFocusable = contentNode.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
    firstFocusable?.focus();
  }, [contentNode]);

  return { contentRef };
};

export default useOverlayFocus;
